// Buscador rápido de clientes (F2): por RUT, razón social o nombre de fantasía, en el servidor, con
// debounce y PAGINADO (D-UI-2): ↓ en la última fila trae la página siguiente. ↑ ↓ eligen, Enter toma,
// Esc cierra sin cambiar nada.
import { useState } from 'react';
import {
  useBuscarClientes,
  useDebounce,
  type OrdenClientes,
  type ResultadoPaginado,
} from '../api/consultas';
import type { ClienteEncontrado } from '../api/contratos';
import { Modal } from '../comun/Modal';
import { NOMBRE_CONDICION, rut } from '../comun/formato';
import { PanelError } from '../comun/Estados';

export function ListaClientes({
  r,
  texto,
  sel,
  alElegir,
  alSeleccionar,
  plural = 'clientes',
}: {
  r: ResultadoPaginado<ClienteEncontrado>;
  texto: string;
  sel: number;
  alElegir: (c: ClienteEncontrado) => void;
  alSeleccionar: (i: number) => void;
  plural?: 'clientes' | 'proveedores';
}) {
  if (r.isError) return <PanelError error={r.error} />;
  return (
    <div
      className="grilla-caja buscador-lista"
      onScroll={(e) => {
        const c = e.currentTarget;
        if (r.hayMas && c.scrollTop + c.clientHeight >= c.scrollHeight - 40) r.cargarMas();
      }}
    >
      <table
        className="grilla"
        aria-label={plural === 'clientes' ? 'Clientes encontrados' : 'Proveedores encontrados'}
        aria-busy={r.isFetching}
      >
        <thead>
          <tr>
            <th style={{ width: '9rem' }}>RUT</th>
            <th>Razón social</th>
            <th>Nombre de fantasía</th>
            <th style={{ width: '7rem' }}>Condición</th>
            <th style={{ width: '9rem' }}>Estado</th>
          </tr>
        </thead>
        <tbody>
          {r.filas.map((c, i) => (
            <tr
              key={c.id}
              aria-selected={i === sel}
              className={i === sel ? 'fila-sel' : undefined}
              ref={i === sel ? (el) => el?.scrollIntoView?.({ block: 'nearest' }) : undefined}
              onMouseMove={(e) => {
                // Solo un movimiento REAL del mouse elige: la lista que se desplaza con ↓ bajo un puntero
                // quieto no debe robarle la selección al teclado.
                if (e.movementX || e.movementY) alSeleccionar(i);
              }}
              onClick={() => alElegir(c)}
            >
              <td className="mono">{rut(c.rut, c.dv)}</td>
              <td>{c.razon_social}</td>
              <td>{c.nombre_fantasia ?? ''}</td>
              <td>{NOMBRE_CONDICION[c.condicion_pago ?? ''] ?? c.condicion_pago ?? '—'}</td>
              <td>
                {c.bloqueado ? (
                  <span className="insignia tono-malo">Bloqueado</span>
                ) : c.estado !== 'activa' ? (
                  <span className="insignia tono-aviso">{c.estado}</span>
                ) : (
                  <span className="insignia tono-ok">Activo</span>
                )}
              </td>
            </tr>
          ))}
          {!r.filas.length && (
            <tr>
              <td colSpan={5} className="grilla-vacia">
                {texto.trim().length < 2
                  ? 'Escriba al menos 2 caracteres (RUT, razón social o nombre)'
                  : r.isFetching
                    ? 'Buscando…'
                    : `Sin ${plural} con ese criterio en esta empresa`}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {r.filas.length > 0 && (
        <div className="grilla-pie">
          <span className="apagado" data-conteo>
            {r.filas.length} {r.hayMas ? 'mostrados · hay más' : 'en total'}
          </span>
          {r.hayMas && (
            <button type="button" disabled={r.cargandoMas} onClick={r.cargarMas}>
              {r.cargandoMas ? 'Cargando…' : 'Cargar más (↓ al final)'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function BuscadorCliente({
  empresaId,
  textoInicial = '',
  alElegir,
  alCerrar,
  alNuevo,
  rol = 'cliente',
}: {
  empresaId: string;
  /** Proveedores (Pago a Proveedores) usan el mismo buscador de entidades. */
  rol?: 'cliente' | 'proveedor';
  /** Lo que se escribió en el campo del código (un nombre, o un código que no calzó). */
  textoInicial?: string;
  alElegir: (c: ClienteEncontrado) => void;
  alCerrar: () => void;
  /** Si viene, ofrece crear un cliente nuevo (recibe lo escrito, por si es un RUT). */
  alNuevo?: ((texto: string) => void) | undefined;
}) {
  const [texto, setTexto] = useState(textoInicial);
  const [orden, setOrden] = useState<OrdenClientes>('razon_social');
  const [sel, setSel] = useState(0);
  const r = useBuscarClientes(empresaId, useDebounce(texto), orden, rol);
  const nombre = rol === 'proveedor' ? 'proveedor' : 'cliente';
  const filas = r.filas;
  return (
    <Modal
      titulo={rol === 'proveedor' ? 'Buscar proveedor' : 'Buscar cliente (F2)'}
      alCerrar={alCerrar}
      ancho="ancho"
    >
      <div className="fila-datos">
        <input
          data-autofoco
          className="buscador"
          placeholder="RUT, razón social o nombre de fantasía…"
          aria-label={`Buscar ${nombre}`}
          value={texto}
          onChange={(e) => {
            setTexto(e.target.value);
            setSel(0);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              if (sel >= filas.length - 1) r.cargarMas();
              setSel((s) => Math.min(filas.length - 1, s + 1));
            } else if (e.key === 'ArrowUp') setSel((s) => Math.max(0, s - 1));
            else if (e.key === 'PageDown') r.cargarMas();
            else if (e.key === 'Enter' && filas[sel]) alElegir(filas[sel]);
            else if (alNuevo && e.altKey && e.key.toLowerCase() === 'u') alNuevo(texto);
            else return;
            e.preventDefault();
          }}
        />
        <label className="campo">
          <span className="etiqueta">Orden</span>
          <select
            aria-label="Orden de clientes"
            value={orden}
            onChange={(e) => {
              setOrden(e.target.value as OrdenClientes);
              setSel(0);
            }}
          >
            <option value="razon_social">Razón social</option>
            <option value="rut">RUT</option>
          </select>
        </label>
        {alNuevo && (
          <button type="button" className="primario" onClick={() => alNuevo(texto)}>
            + Cliente nuevo (Alt+U)
          </button>
        )}
      </div>
      <ListaClientes
        r={r}
        texto={texto}
        sel={sel}
        alElegir={alElegir}
        alSeleccionar={setSel}
        plural={rol === 'proveedor' ? 'proveedores' : 'clientes'}
      />
      <p className="ayuda">
        ↑ ↓ elegir · ↓ al final o AvPág traen más · Enter tomar · Esc cerrar · la búsqueda es en el
        servidor
      </p>
    </Modal>
  );
}
